import { Link, useLocation } from "react-router-dom";
import { ChevronRight, PlusCircleIcon, ScrollText } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

function pathIsActive(pathname, url, end) {
  if (!url || url === "#") return false;
  if (end || url === "/") return pathname === url;
  return pathname === url || pathname.startsWith(`${url}/`);
}

function groupIsActive(pathname, item) {
  if (item.isActive) return true;
  if (item.url && pathIsActive(pathname, item.url, item.end)) return true;
  return (item.items || []).some((sub) => pathIsActive(pathname, sub.url));
}

export function NavMain({
  items = [],
  label,
  quickCreateTo,
  quickCreateLabel = "Quick Create",
}) {
  const { pathname } = useLocation();

  return (
    <SidebarGroup>
      {label ? <SidebarGroupLabel>{label}</SidebarGroupLabel> : null}
      <SidebarGroupContent className="flex flex-col gap-2">
        {quickCreateTo ? (
          <SidebarMenu>
            <SidebarMenuItem className="flex items-center gap-2">
              <SidebarMenuButton
                asChild
                tooltip={quickCreateLabel}
                className="min-w-8 bg-primary text-primary-foreground duration-200 ease-linear hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/90 active:text-primary-foreground"
              >
                <Link to={quickCreateTo}>
                  <PlusCircleIcon />
                  <span>{quickCreateLabel}</span>
                </Link>
              </SidebarMenuButton>
              <Button
                size="icon"
                className="h-9 w-9 shrink-0 group-data-[collapsible=icon]:opacity-0"
                variant="outline"
                asChild
              >
                <Link to="/audit-logs" title="Audit Logs">
                  <ScrollText />
                  <span className="sr-only">Audit Logs</span>
                </Link>
              </Button>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : null}

        <SidebarMenu>
          {items.map((item) => {
            const hasChildren = Array.isArray(item.items) && item.items.length > 0;
            const Icon = item.icon;

            if (!hasChildren) {
              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.title}
                    isActive={pathIsActive(pathname, item.url, item.end)}
                  >
                    <Link to={item.url}>
                      {Icon ? <Icon className={cn(item.iconClassName)} /> : null}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            }

            const openByDefault = groupIsActive(pathname, item);

            return (
              <Collapsible
                key={item.title}
                asChild
                defaultOpen={openByDefault}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={item.title}>
                      {Icon ? <Icon className={cn(item.iconClassName)} /> : null}
                      <span>{item.title}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={pathIsActive(pathname, subItem.url, subItem.end)}
                            >
                              <Link to={subItem.url}>
                                {SubIcon ? (
                                  <SubIcon className={cn(item.iconClassName)} />
                                ) : null}
                                <span>{subItem.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
